import 'element-internals-polyfill';
import { AttachInternals, Component, Element, Listen, Prop, State, Watch, h, Method } from '@stencil/core';
import '@material/web/select/outlined-select';
import { awaitDefinedResult, getOverriddenValidationMessage, selectFormSubmitters } from '../../utils/utils';
import { libraryComponentOptions } from '../../settings/settings';

/** A form input element for selecting from a fixed set of options, similar to `<select>`.
 * @slot - The default slot is for `<omk-select-option>` elements representing the choices of values.
 * Emits `change` and `input` events when the user makes a selection.
 */
@Component({
  tag: 'omk-select',
  styleUrl: 'omk-select.css',
  shadow: { delegatesFocus: true },
  formAssociated: true,
})
export class OmkSelect {
  /** Label displayed to the user for the select. */
  @Prop() label: string;
  /** Value for the selected option, which is submitted with the associated form. */
  @Prop({ mutable: true }) value: string;
  /** Name for the component. The name is used to reference the component in javascript or to reference form data if the component is part of a form. */
  @Prop() name: string = '';
  /** When disabled, the field is grayed out, user interaction is disabled, and the value is excluded from any containing form. */
  @Prop({ reflect: true }) disabled: boolean = false;
  /** When required, a selection must be made before the associated form can be submitted.
   * The validation message will only appear once the element is touched or an attempt is made to submit the form by default.
   * `alwaysShowValidationMessage` and `hideValidationMessage` alter this behaviour. */
  @Prop() required: boolean = false;
  /** Sets a custom validation message and marks the select as invalid for form purposes. When set through the JS property, the error message will be shown immediately.
   * When set through the HTML attribute, the validation message will only appear once the element is touched or an attempt is made to submit the form. */
  @Prop() customValidationMessage: string;
  /** Optional user-facing description of the field. */
  @Prop() supportingText: string;
  /** @internal Do not set - regard as read-only. Forms check this property before submit and when checkValidity() is called. */
  @Prop({ mutable: true }) validity: ValidityState;
  /** @internal Do not set - regard as read-only. The current validation message whether custom or built-in. */
  @Prop({ mutable: true }) validationMessage: string;
  /** Show validation message immediately instead of waiting for user interaction or form submission. */
  @Prop() alwaysShowValidationMessage: boolean;
  /** Hide the inline validation messages. Used when error messages are to be displayed using other means other than the inline capability. */
  @Prop() hideValidationMessage: boolean;
  @AttachInternals() internals: ElementInternals;
  @State() touched: boolean = false;

  @Element() host;

  innerMaterialSelect;

  private initialValue: string;

  @Listen('request-selection')
  handleRequestSelection(event) {
    this.value = event.target.value;
    this.internals.setFormValue(event.target.value);
    this.validate().then(() => {
      this.emitUserSelectionEvents();
    });
  }

  @Listen('request-deselection')
  handleRequestDeselection(event) {
    if (event.target.value === this.value) {
      this.value = '';
      this.internals.setFormValue('');
      this.validate().then(() => {
        this.emitUserSelectionEvents();
      });
    }
  }

  emitUserSelectionEvents() {
    this.host.dispatchEvent(new Event('change', { composed: true, bubbles: true, cancelable: false }));
    // On native `select` elements `input` events are base `Event`s not `InputEvent`s with props like `data` and `inputType`, so we immitate that here.
    this.host.dispatchEvent(new Event('input', { composed: true, bubbles: true, cancelable: false }));
  }

  @Listen('omk-select-option-initial-value')
  handleValueInitializer(event) {
    event.stopPropagation();
    if (event.detail) {
      this.value = event.detail;
      this.internals.setFormValue(event.detail);
    }
  }

  handleUserInteraction() {
    this.touched = true;
  }

  handleInnerChange(event: Event) {
    // this comes too early - we don't have the updated value yet
    event.stopPropagation();
  }

  @Watch('customValidationMessage')
  @Watch('alwaysShowValidationMessage')
  handleValidationPropChange(newValue) {
    if (newValue) {
      this.validate(true);
    }
  }

  @Watch('value')
  handleDirectValueChange(newValue) {
    // changes bubbling up will change material select first, so don't bother with this check in those cases
    if (!this.innerMaterialSelect || newValue !== this.innerMaterialSelect?.value) {
      const possibleValues = Array.from(this.host.shadowRoot.querySelector('slot')?.assignedElements() ?? []).map(
        (e: HTMLOmkSelectOptionElement) => e.value,
      );
      // If no option matches, default to empty like a native select does
      if (!possibleValues.includes(newValue)) {
        this.value = '';
        this.internals.setFormValue('');
        this.validate();
      } else {
        this.internals.setFormValue(newValue);
        this.validate();
      }
    }
  }

  /** Reset the select to its default value. */
  @Method()
  async reset() {
    this.value = this.initialValue;
    this.touched = false;
    this.internals.setFormValue(this.value);

    // Reset the selection in the slot options
    const assignedOptions = Array.from(this.host.querySelectorAll('omk-select-option')) as HTMLOmkSelectOptionElement[];
    assignedOptions.forEach((option: HTMLOmkSelectOptionElement) => {
      option.selected = option.value === this.initialValue;
    });

    if (this.innerMaterialSelect) {
      this.innerMaterialSelect.value = this.initialValue;
      const materialInput = this.innerMaterialSelect.shadowRoot.querySelector('input');
      if (materialInput) {
        materialInput.value = this.initialValue;
      }
    }

    await this.validate();
  }

  componentWillLoad() {
    this.initialValue = this.host.getAttribute('value') || '';
    this.value = this.initialValue;
  }

  componentDidLoad() {
    this.handleDirectValueChange(this.value);
  }

  async validate(setTouched?: boolean) {
    if (setTouched) {
      this.touched = true;
    }
    if (!this.innerMaterialSelect) {
      // initial render - we need to wait for inner component to get to a consistent state
      this.innerMaterialSelect = await awaitDefinedResult(() =>
        this.host?.shadowRoot?.getElementById('materialSelect'),
      );
    }
    if (this.disabled) return true;
    if (this.customValidationMessage) {
      this.internals.setValidity(
        { customError: true },
        this.customValidationMessage,
        this.innerMaterialSelect?.shadowRoot?.getElementById('input') ?? undefined,
      );
    } else {
      this.internals.setValidity(
        this.innerMaterialSelect.validity,
        getOverriddenValidationMessage(
          libraryComponentOptions.componentValidationMessageOverrides['omk-select'],
          this.innerMaterialSelect.validity,
          this.innerMaterialSelect.validationMessage,
          { value: this.value, valueLength: this.value?.length },
        ),
        this.innerMaterialSelect?.shadowRoot?.getElementById('input') ?? undefined,
      );
    }
    this.validity = this.internals?.validity;

    // force a re-render by setting a new reference
    this.validationMessage = this.internals?.validationMessage;
    return this.validity?.valid;
  }

  async componentDidRender() {
    await this.validate();
  }

  render() {
    return (
      <md-outlined-select
        id="materialSelect"
        value={this.value}
        required={this.required}
        label={this.label}
        disabled={this.disabled}
        supportingText={this.supportingText}
        errorText={this.hideValidationMessage ? '' : this.validationMessage}
        error={!!((this.touched || this.alwaysShowValidationMessage) && this.validationMessage)}
        onChange={this.handleInnerChange.bind(this)}
        onInput={this.handleInnerChange.bind(this)}
        onClosing={this.handleUserInteraction.bind(this)}
      >
        <slot></slot>
      </md-outlined-select>
    );
  }

  formAssociatedCallback(form) {
    selectFormSubmitters(form).forEach(submitter =>
      submitter.addEventListener('click', () => {
        this.validate(true);
      }),
    );
  }
}
